import i18n from 'widget/i18n/index';
import {
  ALL_SLOT_OPTIONS,
  DEFAULT_SLOT_OPTIONS,
  HOUR_SLOT_OPTIONS,
  DAYPART_SLOT_OPTIONS,
  normalizeSlotOptions,
  slotOptionsFromDurationMode,
  isDaypartSlotOption,
  hoursFromSlotOption,
} from 'shared/helpers/bookingSlotOptions';

export {
  ALL_SLOT_OPTIONS,
  DEFAULT_SLOT_OPTIONS,
  HOUR_SLOT_OPTIONS,
  DAYPART_SLOT_OPTIONS,
  normalizeSlotOptions,
  slotOptionsFromDurationMode,
  isDaypartSlotOption,
  hoursFromSlotOption,
};

const defaultTranslations = Object.fromEntries(
  Object.entries(i18n).filter(([key]) => key.includes('en'))
).en;

export const standardFieldKeys = {
  emailAddress: {
    key: 'EMAIL_ADDRESS',
    label: 'Email Address',
    placeholder: 'Please enter your email address',
  },
  fullName: {
    key: 'FULL_NAME',
    label: 'Full Name',
    placeholder: 'Please enter your full name',
  },
  phoneNumber: {
    key: 'PHONE_NUMBER',
    label: 'Phone Number',
    placeholder: 'Please enter your phone number',
  },
  space_id: {
    key: 'SPACE',
    label: 'Spaces',
    placeholder: 'Please select a space',
  },
  event_space_id: {
    key: 'EVENT_SPACE',
    label: 'Spaces',
    placeholder: 'Please select a space',
  },
  pax: {
    key: 'PAX',
    label: 'Guests',
    placeholder: 'Number of guests',
  },
  booking_datetime: {
    key: 'BOOKING_DATETIME',
    label: 'Date & time',
    placeholder: 'Select a date and time',
  },
  message: {
    key: 'MESSAGE',
    label: 'Message',
    placeholder: 'Please enter your message',
  },
};

export const MESSAGE_FIELD_DEFAULTS = {
  name: 'message',
  type: 'textarea',
  field_type: 'standard',
  required: true,
  enabled: true,
};

export const SPACE_FIELD_DEFAULTS = {
  name: 'space_id',
  type: 'space',
  field_type: 'standard',
  values: [],
  required: false,
  enabled: false,
  selection_mode: 'single',
};

// Event booking spaces — drives allocations (separate from conversation Spaces)
export const EVENT_SPACE_FIELD_DEFAULTS = {
  name: 'event_space_id',
  type: 'space',
  field_type: 'event_attribute',
  values: [],
  required: false,
  enabled: false,
  selection_mode: 'single',
  enforce_availability: true,
};

export const PAX_FIELD_DEFAULTS = {
  name: 'pax',
  type: 'number',
  field_type: 'event_attribute',
  required: false,
  enabled: false,
  enforce_availability: true,
};

export const BOOKING_DATETIME_FIELD_DEFAULTS = {
  name: 'booking_datetime',
  type: 'booking_datetime',
  field_type: 'event_attribute',
  required: false,
  enabled: false,
  enforce_availability: true,
  slot_options: DEFAULT_SLOT_OPTIONS,
};

export const BOOKING_DAYPART_OPTIONS = [
  { id: 'morning', label: 'Morning' },
  { id: 'afternoon', label: 'Afternoon' },
  { id: 'evening', label: 'Evening' },
  { id: 'full_day', label: 'Full day' },
];

export const BOOKING_FIELD_NAMES = new Set([
  'event_space_id',
  'pax',
  'booking_datetime',
]);

export const fieldEnforcesAvailability = field =>
  field?.enforce_availability !== false;

export const isAvailabilityConfigurableField = field =>
  field?.name === 'pax' ||
  isEventSpaceField(field) ||
  isBookingDatetimeField(field);

export const isBookingDateField = field =>
  field?.name === 'booking_date' || field?.type === 'booking_date';

export const isBookingDatetimeField = field =>
  field?.name === 'booking_datetime' || field?.type === 'booking_datetime';

export const isBookingDurationField = field =>
  field?.name === 'booking_duration' || field?.type === 'booking_duration';

/** Migrate legacy booking_duration onto datetime.slot_options, then drop duration. */
export const migrateDurationOntoDatetime = fields => {
  const list = Array.isArray(fields) ? fields : [];
  const duration = list.find(isBookingDurationField);

  const next = list
    .filter(f => !isBookingDurationField(f))
    .map(f => {
      if (!isBookingDatetimeField(f)) return f;
      if (Array.isArray(f.slot_options) && f.slot_options.length) {
        return { ...f, slot_options: normalizeSlotOptions(f.slot_options) };
      }
      if (duration) {
        return {
          ...f,
          slot_options: slotOptionsFromDurationMode(duration.duration_mode),
        };
      }
      return {
        ...f,
        slot_options: normalizeSlotOptions(f.slot_options),
      };
    });

  return next;
};

/** Drop legacy date-only + duration; migrate duration onto datetime.slot_options. */
export const migrateBookingFields = fields => {
  const withoutDate = (Array.isArray(fields) ? fields : []).filter(
    f => !isBookingDateField(f)
  );
  return migrateDurationOntoDatetime(withoutDate);
};

export const isEventSpaceField = field =>
  field?.name === 'event_space_id' ||
  ((field?.type === 'space' || field?.type === 'spaces') &&
    field?.field_type === 'event_attribute');

export const isConversationSpaceField = field => {
  if (isEventSpaceField(field)) return false;
  return (
    field?.name === 'space_id' ||
    field?.name === 'spaces' ||
    field?.type === 'space' ||
    field?.type === 'spaces'
  );
};

export const ensureMessageField = fields => {
  const existing = fields.find(f => f.name === 'message');
  if (existing) {
    return fields.map(f =>
      f.name === 'message'
        ? {
            ...f,
            ...MESSAGE_FIELD_DEFAULTS,
            enabled: true,
            fieldset_id: null,
            required: f.required ?? MESSAGE_FIELD_DEFAULTS.required,
            label: f.label || getLabel({ key: 'message', label: 'Message' }),
            placeholder:
              f.placeholder ||
              getPlaceHolder({
                key: 'message',
                placeholder: 'Please enter your message',
              }),
          }
        : f
    );
  }

  return [
    ...fields,
    {
      ...MESSAGE_FIELD_DEFAULTS,
      label: getLabel({ key: 'message', label: 'Message' }),
      placeholder: getPlaceHolder({
        key: 'message',
        placeholder: 'Please enter your message',
      }),
    },
  ];
};

export const pinMessageFieldLast = fields => {
  const messageField = fields.find(f => f.name === 'message');
  if (!messageField) return fields;

  return [...fields.filter(f => f.name !== 'message'), messageField];
};

export const getLabel = ({ key, label }) => {
  return defaultTranslations.PRE_CHAT_FORM.FIELDS[key]
    ? defaultTranslations.PRE_CHAT_FORM.FIELDS[key].LABEL
    : standardFieldKeys[key]?.label || label;
};
export const getPlaceHolder = ({ key, placeholder }) => {
  return defaultTranslations.PRE_CHAT_FORM.FIELDS[key]
    ? defaultTranslations.PRE_CHAT_FORM.FIELDS[key].PLACEHOLDER
    : standardFieldKeys[key]?.placeholder || placeholder;
};

export const getCustomFields = ({ standardFields, customAttributes }) => {
  let customFields = [];
  const { pre_chat_fields: preChatFields } = standardFields;
  customAttributes.forEach(attribute => {
    const itemExist = preChatFields.find(
      item => item.name === attribute.attribute_key
    );
    if (!itemExist) {
      customFields.push({
        label: attribute.attribute_display_name,
        placeholder: attribute.attribute_display_name,
        name: attribute.attribute_key,
        type: attribute.attribute_display_type,
        values: attribute.attribute_values,
        field_type: attribute.attribute_model,
        regex_pattern: attribute.regex_pattern,
        regex_cue: attribute.regex_cue,
        required: false,
        enabled: false,
      });
    }
  });
  return customFields;
};

export const isSpaceField = field =>
  isEventSpaceField(field) || isConversationSpaceField(field);

export const isBookingField = field =>
  BOOKING_FIELD_NAMES.has(field?.name) ||
  isEventSpaceField(field) ||
  isBookingDatetimeField(field);

export const normalizeSpaceField = field => {
  if (!isSpaceField(field)) return field;

  const isEvent = isEventSpaceField(field) || field?.name === 'event_space_id';
  const defaults = isEvent ? EVENT_SPACE_FIELD_DEFAULTS : SPACE_FIELD_DEFAULTS;

  return {
    ...field,
    ...defaults,
    enabled: field.enabled ?? false,
    required: field.required ?? false,
    fieldset_id: field.fieldset_id ?? null,
    field_type: isEvent ? 'event_attribute' : 'standard',
    name: isEvent ? 'event_space_id' : 'space_id',
    type: 'space',
    selection_mode: field.selection_mode === 'multi' ? 'multi' : 'single',
    enforce_availability: isEvent
      ? field.enforce_availability !== false
      : undefined,
    label:
      field.label ||
      getLabel({
        key: isEvent ? 'event_space_id' : 'space_id',
        label: 'Spaces',
      }),
    placeholder:
      field.placeholder ||
      getPlaceHolder({
        key: isEvent ? 'event_space_id' : 'space_id',
        placeholder: 'Please select a space',
      }),
    values: Array.isArray(field.values) ? field.values : [],
  };
};

export const normalizeStandardBookingField = field => {
  if (isSpaceField(field)) return normalizeSpaceField(field);

  if (field?.name === 'pax') {
    return {
      ...PAX_FIELD_DEFAULTS,
      ...field,
      name: 'pax',
      type: 'number',
      field_type: 'event_attribute',
      enforce_availability: field.enforce_availability !== false,
      label: field.label || getLabel({ key: 'pax', label: 'Guests' }),
      placeholder:
        field.placeholder ||
        getPlaceHolder({ key: 'pax', placeholder: 'Number of guests' }),
    };
  }

  if (
    field?.name === 'booking_datetime' ||
    field?.type === 'booking_datetime'
  ) {
    return {
      ...BOOKING_DATETIME_FIELD_DEFAULTS,
      ...field,
      name: 'booking_datetime',
      type: 'booking_datetime',
      field_type: 'event_attribute',
      enforce_availability: field.enforce_availability !== false,
      slot_options: normalizeSlotOptions(field.slot_options),
      label:
        field.label ||
        getLabel({ key: 'booking_datetime', label: 'Date & time' }),
      placeholder:
        field.placeholder ||
        getPlaceHolder({
          key: 'booking_datetime',
          placeholder: 'Select a date and time',
        }),
    };
  }

  return field;
};

const STANDARD_FIELDS_TO_ENSURE = [
  SPACE_FIELD_DEFAULTS,
  EVENT_SPACE_FIELD_DEFAULTS,
  PAX_FIELD_DEFAULTS,
  BOOKING_DATETIME_FIELD_DEFAULTS,
];

const appliesLegacyEnforceOff = field =>
  field?.enforce_availability === undefined &&
  (field?.name === 'event_space_id' ||
    field?.name === 'booking_datetime' ||
    field?.name === 'pax' ||
    ((field?.type === 'space' || field?.type === 'spaces') &&
      field?.field_type === 'event_attribute'));

export const getFormattedPreChatFields = ({
  preChatFields = [],
  legacyEnforceAvailability,
}) => {
  const migrated = migrateBookingFields(preChatFields);
  return migrated.map(item => {
    const withLegacy =
      legacyEnforceAvailability === false && appliesLegacyEnforceOff(item)
        ? { ...item, enforce_availability: false }
        : item;
    const normalized = normalizeStandardBookingField(withLegacy);
    return {
      ...normalized,
      label:
        normalized.label ||
        getLabel({ key: normalized.name, label: normalized.name }),
      placeholder:
        normalized.placeholder ||
        getPlaceHolder({ key: normalized.name, placeholder: normalized.name }),
    };
  });
};

export const getPreChatFields = ({
  preChatFormOptions = {},
  customAttributes = [],
}) => {
  const {
    pre_chat_message,
    submit_button_label,
    pre_chat_fields,
    pre_chat_fieldsets,
    enforce_availability,
    required_before_chat,
  } = preChatFormOptions;
  let customFields = {};
  let preChatFields = {};

  const formattedPreChatFields = getFormattedPreChatFields({
    preChatFields: pre_chat_fields || [],
    legacyEnforceAvailability: enforce_availability,
  });

  const ensuredStandardFields = STANDARD_FIELDS_TO_ENSURE.filter(
    f => !formattedPreChatFields.find(existing => existing.name === f.name)
  ).map(f =>
    normalizeStandardBookingField({
      ...f,
      label: getLabel({ key: f.name, label: f.name }),
      placeholder: getPlaceHolder({ key: f.name, placeholder: f.name }),
    })
  );

  customFields = getCustomFields({
    standardFields: { pre_chat_fields: formattedPreChatFields },
    customAttributes,
  });
  preChatFields = pinMessageFieldLast(
    ensureMessageField([
      ...formattedPreChatFields,
      ...ensuredStandardFields,
      ...customFields,
    ])
  );

  return {
    pre_chat_message,
    submit_button_label,
    required_before_chat: required_before_chat !== false,
    pre_chat_fields: preChatFields,
    pre_chat_fieldsets: pre_chat_fieldsets || [],
  };
};
